import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaWhatsapp, FaPhone, FaFileAlt, FaTimes, FaChevronUp } from 'react-icons/fa';

export default function FloatingCTA() {
  const [isVisible, setIsVisible] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const show = window.scrollY > 300;
      setIsVisible(show);
      if (!show) setMenuOpen(false);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const actionItems = [
    {
      icon: <FaWhatsapp className="w-5 h-5" />,
      label: 'Chat on WhatsApp',
      href: 'https://wa.me/919609600600?text=Hi%20Green%20Gusto%20Energy,%20I%20would%20like%20to%20enquire%20about%20your%20power%20solutions.',
      bg: 'bg-[#25D366] hover:bg-[#1eab55]',
      external: true,
    },
    {
      icon: <FaPhone className="w-4 h-4" />,
      label: 'Call Emergency Support',
      href: 'tel:+919609600600',
      bg: 'bg-primary hover:bg-primary-dark',
      external: false,
    },
    {
      icon: <FaFileAlt className="w-4 h-4" />,
      label: 'Request a Quote',
      to: '/contact#enquiry',
      bg: 'bg-[#0F5A3A] hover:bg-[#0A3F29]',
      isLink: true,
    },
  ];

  const itemVariants = {
    hidden: { scale: 0, opacity: 0, y: 12 },
    visible: (i) => ({
      scale: 1, opacity: 1, y: 0,
      transition: { duration: 0.22, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] }
    }),
    exit: (i) => ({
      scale: 0, opacity: 0, y: 8,
      transition: { duration: 0.15, delay: i * 0.03 }
    }),
  };

  return (
    <div className="fixed bottom-6 right-6 z-40 flex flex-col items-center gap-3">

      {/* Action Items */}
      <AnimatePresence>
        {menuOpen && (
          <div className="flex flex-col items-center gap-3">
            {actionItems.map((item, i) => (
              <motion.div
                key={item.label}
                custom={actionItems.length - 1 - i}
                variants={itemVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="relative group"
              >
                {/* Tooltip */}
                <span className="absolute right-14 top-1/2 -translate-y-1/2 px-3 py-1.5 bg-charcoal text-white text-xs font-medium rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none whitespace-nowrap shadow-premium" style={{ background: 'rgba(12, 26, 18, 0.92)' }}>
                  {item.label}
                  <span className="absolute right-[-4px] top-1/2 -translate-y-1/2 w-2 h-2 rotate-45" style={{ background: 'rgba(12, 26, 18, 0.92)' }} />
                </span>

                {item.isLink ? (
                  <Link
                    to={item.to}
                    className={`w-11 h-11 rounded-full ${item.bg} text-white flex items-center justify-center shadow-premium transition-all duration-200 hover:-translate-y-0.5 hover:shadow-gold`}
                  >
                    {item.icon}
                  </Link>
                ) : (
                  <a
                    href={item.href}
                    target={item.external ? '_blank' : undefined}
                    rel={item.external ? 'noopener noreferrer' : undefined}
                    className={`w-11 h-11 rounded-full ${item.bg} text-white flex items-center justify-center shadow-premium transition-all duration-200 hover:-translate-y-0.5`}
                  >
                    {item.icon}
                  </a>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </AnimatePresence>

      {/* Main Trigger Button */}
      <motion.button
        onClick={() => setMenuOpen(!menuOpen)}
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.92 }}
        className="w-14 h-14 rounded-full text-white flex items-center justify-center shadow-premium cursor-pointer relative select-none focus:outline-none focus:ring-2 focus:ring-primary/40 focus:ring-offset-2"
        style={{ background: 'linear-gradient(135deg, #0F5A3A, #1C8C5B)' }}
        aria-label="Quick contact"
      >
        {/* Pulse ring */}
        <span className="absolute inset-0 rounded-full animate-ping opacity-15" style={{ background: 'linear-gradient(135deg, #0F5A3A, #1C8C5B)' }} />
        <AnimatePresence mode="wait">
          {menuOpen
            ? <motion.div key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.15 }}>
              <FaTimes className="w-5 h-5 relative z-10" />
            </motion.div>
            : <motion.div key="help" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.15 }} className="relative z-10 text-center">
              <span className="block text-[9px] font-bold tracking-wide leading-none">NEED</span>
              <span className="block text-[8px] font-extrabold tracking-wider">HELP</span>
            </motion.div>
          }
        </AnimatePresence>
      </motion.button>

      {/* Back to Top */}
      <AnimatePresence>
        {isVisible && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={scrollToTop}
            className="w-10 h-10 rounded-full bg-white border border-border text-primary hover:text-accent hover:border-primary/30 hover:shadow-card-hover flex items-center justify-center shadow-card transition-all duration-200 cursor-pointer"
            aria-label="Back to top"
          >
            <FaChevronUp className="w-3.5 h-3.5" />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}
